import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Search, X, Youtube, Instagram, Facebook, Twitter, Music2 } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_ITEMS, SOCIALS } from "@/lib/constants";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const SOCIAL_ICONS = { youtube: Youtube, instagram: Instagram, facebook: Facebook, tiktok: Music2, x: Twitter };

function SocialRow({ className = "" }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {SOCIALS.map((s) => {
        const Icon = SOCIAL_ICONS[s.key];
        const hasUrl = !!s.url;
        return (
          <a key={s.key} href={hasUrl ? s.url : "#"} aria-label={s.name} data-testid={`social-${s.key}`}
            target={hasUrl ? "_blank" : undefined} rel={hasUrl ? "noopener noreferrer" : undefined}
            className="text-slate-400 hover:text-nx-orange transition-colors"
            onClick={(e) => { if (!hasUrl) e.preventDefault(); }}>
            <Icon size={16} strokeWidth={1.8} />
          </a>
        );
      })}
    </div>
  );
}

export default function Header() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/buscar?q=${encodeURIComponent(q.trim())}`); setSearchOpen(false); setQ(""); }
  };

  const isActive = (path) => path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <header className="sticky top-0 z-50 bg-nx-dark border-b border-white/10" data-testid="site-header">
      {/* top strip */}
      <div className="hidden lg:block border-b border-white/5">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-9">
          <div className="flex items-center gap-2.5 text-[11px] tracking-wider text-slate-400 uppercase">
            <span className="flex items-center gap-1.5 text-slate-300">
              <span className="nx-live-dot inline-block w-2 h-2 rounded-full bg-red-500" />
              <span className="font-semibold">EN VIVO</span>
            </span>
            <span className="text-slate-600">·</span>
            <span>Medio de comunicación independiente</span>
          </div>
          <SocialRow />
        </div>
      </div>

      {/* main bar */}
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-[74px] gap-4">
          <Link to="/" data-testid="header-home-link"><Logo /></Link>

          <div className="flex items-center gap-2">
            <button onClick={() => setSearchOpen((v) => !v)} data-testid="search-toggle"
              aria-label="Buscar" className="p-2 text-slate-300 hover:text-nx-orange transition-colors">
              {searchOpen ? <X size={20} /> : <Search size={20} />}
            </button>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 text-slate-300 hover:text-nx-orange" aria-label="Menú" data-testid="mobile-menu-toggle">
                  <Menu size={22} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-nx-dark border-white/10 text-white w-[300px] p-0">
                <div className="p-5 border-b border-white/10"><Logo /></div>
                <nav className="flex flex-col p-2">
                  {NAV_ITEMS.map((item) => (
                    <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
                      data-testid={`mobile-nav-${item.path === "/" ? "inicio" : item.path.slice(1)}`}
                      className={`px-4 py-3 text-sm tracking-wide font-medium border-b border-white/5 transition-colors ${isActive(item.path) ? "text-nx-orange" : "text-slate-200 hover:text-nx-orange"}`}>
                      {item.label}
                    </Link>
                  ))}
                  <Link to="/contacto" onClick={() => setMobileOpen(false)}
                    className="px-4 py-3 text-sm tracking-wide font-medium text-slate-200 hover:text-nx-orange" data-testid="mobile-nav-contacto">
                    CONTACTO
                  </Link>
                </nav>
                <div className="p-5 mt-auto"><SocialRow /></div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* search bar */}
        {searchOpen && (
          <form onSubmit={submitSearch} className="pb-3 nx-fade-up" data-testid="search-form">
            <div className="flex items-center gap-2 bg-white/10 rounded px-3 py-2">
              <Search size={18} className="text-slate-400" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                data-testid="search-input" placeholder="Buscar en NEXODUM…"
                className="flex-1 bg-transparent text-white placeholder-slate-400 outline-none text-sm" />
              <button type="submit" data-testid="search-submit"
                className="text-xs font-bold uppercase tracking-wider text-nx-orange hover:text-white">Buscar</button>
            </div>
          </form>
        )}
      </div>

      {/* nav */}
      <nav className="hidden lg:block border-t border-white/5 bg-nx-dark">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <ul className="flex items-center gap-1 h-11">
            {NAV_ITEMS.map((item) => (
              <li key={item.path}>
                <Link to={item.path} data-testid={`nav-${item.path === "/" ? "inicio" : item.path.slice(1)}`}
                  className={`relative px-3 py-2 text-[12.5px] font-semibold tracking-wide transition-colors ${isActive(item.path) ? "text-nx-orange" : "text-slate-200 hover:text-white"}`}>
                  {item.label}
                  {isActive(item.path) && <span className="absolute left-3 right-3 -bottom-[1px] h-0.5 bg-nx-orange" />}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </header>
  );
}
